import { useTheme, Theme, Box } from "@mui/material";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { MarkPlot, LineChart } from "@mui/x-charts/LineChart";
import { MixGroupScheduleRow } from "../millMap/useTenantSchedule";

interface ScheduleDataPoint {
  value: number;
  label: string;
}

interface ScheduleSparklineProps {
  dataPoints: ScheduleDataPoint[];
  mixGroupNames?: string[];
  mixGroupDataPoints?: MixGroupScheduleRow[];
  width?: number;
  height?: number;
}

const EMPTY_STRINGS: string[] = [];
const EMPTY_MIX_GROUP_ROWS: MixGroupScheduleRow[] = [];

const formatScheduleTooltip = (value: number) =>
  `${Math.round(value).toLocaleString()}t`;

const MIX_GROUP_COLORS: Record<string, (t: Theme) => string> = {
  REBAR: (t) => t.palette.data.blue,
  MBQ: (t) => t.palette.data.orange,
  SBQ: (t) => t.palette.data.green,
};

export const ScheduleSparkline = ({
  dataPoints,
  mixGroupNames = EMPTY_STRINGS,
  mixGroupDataPoints = EMPTY_MIX_GROUP_ROWS,
  width = 80,
  height = 32,
}: ScheduleSparklineProps) => {
  const theme = useTheme();
  const color = theme.palette.data.pink;

  if (mixGroupNames.length > 0 && mixGroupDataPoints.length >= 2) {
    const labels = mixGroupDataPoints.map((row) => row.label);
    const series = mixGroupNames.map((name) => ({
      data: mixGroupDataPoints.map((row) => row.values[name] ?? 0),
      stack: "schedule",
      area: true,
      showMark: false,
      curve: "linear" as const,
      color: MIX_GROUP_COLORS[name]?.(theme) ?? color,
      label: name,
      valueFormatter: (v: number | null) => formatScheduleTooltip(v ?? 0),
    }));

    return (
      <Box sx={{ position: "relative", width, height }}>
        <LineChart
          width={width}
          height={height}
          xAxis={[
            { scaleType: "point" as const, data: labels, position: "none" },
          ]}
          yAxis={[{ position: "none" }]}
          series={series}
          hideLegend
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          sx={{
            "& .MuiAreaElement-root": {
              fillOpacity: 0.4,
            },
            "& .MuiMarkElement-root": {
              display: "none",
            },
          }}
        />
        <svg
          width={width}
          height={height}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
          }}
        >
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={height}
            stroke={theme.palette.grey[400]}
            strokeWidth={3}
            strokeDasharray="3 2"
          />
        </svg>
      </Box>
    );
  }

  if (dataPoints.length < 2) {
    return null;
  }

  const values = dataPoints.map((dp) => dp.value);
  const labels = dataPoints.map((dp) => dp.label);

  return (
    <Box sx={{ position: "relative", width, height }}>
      <SparkLineChart
        data={values}
        xAxis={{
          scaleType: "point",
          data: labels,
        }}
        width={width}
        height={height}
        curve="linear"
        area
        showTooltip
        valueFormatter={(value) => formatScheduleTooltip(value ?? 0)}
        slotProps={{
          tooltip: {
            sx: {
              "& .MuiChartsTooltip-mark": {
                borderColor: color,
                backgroundColor: color,
              },
            },
          },
        }}
        sx={{
          "& .MuiLineElement-root": {
            stroke: color,
          },
          "& .MuiAreaElement-root": {
            fill: color,
            fillOpacity: 0.4,
          },
          "& .MuiMarkElement-root": {
            display: "none",
          },
          "& .MuiMarkElement-root:first-of-type": {
            display: "block",
            fill: color,
            stroke: color,
            r: 4,
            transform: "translateX(4px)",
          },
          "& .MuiHighlightElement-root": {
            fill: color,
            stroke: color,
          },
        }}
      >
        <MarkPlot />
      </SparkLineChart>
      <svg
        width={width}
        height={height}
        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
      >
        <line
          x1={0}
          y1={0}
          x2={0}
          y2={height}
          stroke={theme.palette.grey[400]}
          strokeWidth={3}
          strokeDasharray="3 2"
        />
      </svg>
    </Box>
  );
};
